import { useEffect, useMemo, useRef, useState } from "react";
import { IoIosArrowDown, IoIosSearch } from "react-icons/io";
import flagLanguage from "../../assets/images/flags/language.svg";

export const GOOGLE_TRANSLATE_ELEMENT_ID = "google_translate_element";
export const DEFAULT_GOOGLE_LANGUAGE = "en";

const GOOGLE_TRANSLATE_COOKIE_NAME = "googtrans";

const LANGUAGE_TO_COUNTRY_CODE = {
  af: "ZA",
  ak: "GH",
  am: "ET",
  ar: "SA",
  as: "IN",
  ay: "BO",
  az: "AZ",
  be: "BY",
  bg: "BG",
  bm: "ML",
  bn: "BD",
  bho: "IN",
  bs: "BA",
  ca: "AD",
  ceb: "PH",
  ckb: "IQ",
  co: "FR",
  cs: "CZ",
  cy: "GB",
  da: "DK",
  de: "DE",
  doi: "IN",
  dv: "MV",
  ee: "GH",
  el: "GR",
  en: "GB",
  eo: "PL",
  es: "ES",
  et: "EE",
  eu: "ES",
  fa: "IR",
  fi: "FI",
  fil: "PH",
  fr: "FR",
  fy: "NL",
  ga: "IE",
  gd: "GB",
  gl: "ES",
  gn: "PY",
  gom: "IN",
  gu: "IN",
  ha: "NG",
  haw: "US",
  he: "IL",
  hi: "IN",
  hmn: "LA",
  hr: "HR",
  ht: "HT",
  hu: "HU",
  hy: "AM",
  id: "ID",
  ig: "NG",
  ilo: "PH",
  is: "IS",
  it: "IT",
  iw: "IL",
  ja: "JP",
  jw: "ID",
  jv: "ID",
  ka: "GE",
  kk: "KZ",
  km: "KH",
  kn: "IN",
  ko: "KR",
  kri: "SL",
  ku: "IQ",
  ky: "KG",
  la: "VA",
  lb: "LU",
  lg: "UG",
  ln: "CD",
  lo: "LA",
  lt: "LT",
  lus: "IN",
  lv: "LV",
  mai: "IN",
  mg: "MG",
  mi: "NZ",
  mk: "MK",
  ml: "IN",
  mn: "MN",
  "mni-mtei": "IN",
  mr: "IN",
  ms: "MY",
  mt: "MT",
  my: "MM",
  ne: "NP",
  nl: "NL",
  no: "NO",
  nso: "ZA",
  ny: "MW",
  om: "ET",
  or: "IN",
  pa: "IN",
  pl: "PL",
  ps: "AF",
  pt: "PT",
  qu: "PE",
  ro: "RO",
  ru: "RU",
  rw: "RW",
  sa: "IN",
  sd: "PK",
  si: "LK",
  sk: "SK",
  sl: "SI",
  sm: "WS",
  sn: "ZW",
  so: "SO",
  sq: "AL",
  sr: "RS",
  st: "LS",
  su: "ID",
  sv: "SE",
  sw: "KE",
  ta: "IN",
  te: "IN",
  tg: "TJ",
  th: "TH",
  ti: "ER",
  tk: "TM",
  tl: "PH",
  tr: "TR",
  ts: "ZA",
  tt: "RU",
  ug: "CN",
  uk: "UA",
  ur: "PK",
  uz: "UZ",
  vi: "VN",
  xh: "ZA",
  yi: "IL",
  yo: "NG",
  zh: "CN",
  "zh-cn": "CN",
  "zh-tw": "TW",
  zu: "ZA",
};

function normalizeLanguageCode(languageCode) {
  if (typeof languageCode !== "string") {
    return DEFAULT_GOOGLE_LANGUAGE;
  }

  const trimmedLanguageCode = languageCode.trim();
  return trimmedLanguageCode || DEFAULT_GOOGLE_LANGUAGE;
}

function getCountryCodeForLanguage(languageCode) {
  const normalizedLanguageCode = normalizeLanguageCode(languageCode).toLowerCase();
  const baseLanguageCode = normalizedLanguageCode.split("-")[0];

  return (
    LANGUAGE_TO_COUNTRY_CODE[normalizedLanguageCode] ||
    LANGUAGE_TO_COUNTRY_CODE[baseLanguageCode] ||
    ""
  );
}

function hasFlagForLanguage(languageCode) {
  return Boolean(getCountryCodeForLanguage(languageCode));
}

function getFlagForLanguage(languageCode) {
  const countryCode = getCountryCodeForLanguage(languageCode);

  return countryCode
    ? `https://flagcdn.com/${countryCode.toLowerCase()}.svg`
    : flagLanguage;
}

export const GOOGLE_LANGUAGE_OPTIONS = [
  { value: "en", label: "English", flag: getFlagForLanguage("en") },
  { value: "ko", label: "Korean", flag: getFlagForLanguage("ko") },
  { value: "bn", label: "Bengali", flag: getFlagForLanguage("bn") },
  { value: "es", label: "Spanish", flag: getFlagForLanguage("es") },
];

export const GOOGLE_TRANSLATE_STORAGE_KEY = "user_lang";
export const GOOGLE_TRANSLATE_EVENT = "google-translate-language-change";

const syncGoogleTranslateCookie = (languageCode) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  const cookieValue = `/en/${languageCode}`;
  const cookieSettings = [
    `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/`,
    `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;SameSite=Lax`,
  ];

  const hostname = window.location.hostname;
  if (hostname && hostname !== "localhost") {
    cookieSettings.push(
      `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;domain=${hostname};SameSite=Lax`
    );
    cookieSettings.push(
      `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;domain=.${hostname};SameSite=Lax`
    );
  }

  cookieSettings.forEach((cookie) => {
    document.cookie = cookie;
  });
};

const triggerGoogleTranslate = (languageCode) => {
  if (typeof document === "undefined") {
    return false;
  }

  const googleCombo = document.querySelector(".goog-te-combo");
  if (!googleCombo) {
    return false;
  }

  syncGoogleTranslateCookie(languageCode);
  googleCombo.value = languageCode;
  googleCombo.dispatchEvent(new Event("change", { bubbles: true }));
  document.documentElement.lang = languageCode;
  return true;
};

const buildLanguageOption = (languageCode, label) => ({
  value: normalizeLanguageCode(languageCode),
  label: label?.trim() || normalizeLanguageCode(languageCode),
  flag: getFlagForLanguage(languageCode),
});

const getStoredLanguageOptions = () =>
  GOOGLE_LANGUAGE_OPTIONS.map((language) => ({
    ...language,
    value: normalizeLanguageCode(language.value),
  }));

const readGoogleTranslateLanguageOptions = () => {
  if (typeof document === "undefined") {
    return [];
  }

  const googleCombo = document.querySelector(".goog-te-combo");
  if (!googleCombo) {
    return [];
  }

  return Array.from(googleCombo.options)
    .map((option) => ({
      value: normalizeLanguageCode(option.value),
      label: option.textContent?.trim() || "",
    }))
    .filter((option) => option.value && option.label)
    .filter((option) => hasFlagForLanguage(option.value))
    .map((option) => buildLanguageOption(option.value, option.label));
};

const GoogleTranslateSelect = ({
  disabled = false,
  className = "",
  renderWidget = false,
}) => {
  const [currentLang, setCurrentLang] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_GOOGLE_LANGUAGE;
    }

    return normalizeLanguageCode(
      window.localStorage.getItem(GOOGLE_TRANSLATE_STORAGE_KEY) ||
        DEFAULT_GOOGLE_LANGUAGE
    );
  });
  const [availableLanguages, setAvailableLanguages] =
    useState(getStoredLanguageOptions);
  const [hasLoadedTranslatorLanguages, setHasLoadedTranslatorLanguages] =
    useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleLanguageSync = (event) => {
      const nextLanguage = normalizeLanguageCode(
        event?.detail?.language ||
          window.localStorage.getItem(GOOGLE_TRANSLATE_STORAGE_KEY) ||
          DEFAULT_GOOGLE_LANGUAGE
      );

      setCurrentLang(nextLanguage);
    };

    window.addEventListener(GOOGLE_TRANSLATE_EVENT, handleLanguageSync);

    return () => {
      window.removeEventListener(GOOGLE_TRANSLATE_EVENT, handleLanguageSync);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    let pollIntervalId = null;
    let observer = null;

    const syncAvailableLanguages = () => {
      const nextLanguages = readGoogleTranslateLanguageOptions();

      if (!nextLanguages.length) {
        return false;
      }

      setHasLoadedTranslatorLanguages(true);
      setAvailableLanguages((previousLanguages) => {
        const previousSignature = previousLanguages
          .map((language) => `${language.value}:${language.label}`)
          .join("|");
        const nextSignature = nextLanguages
          .map((language) => `${language.value}:${language.label}`)
          .join("|");

        return previousSignature === nextSignature
          ? previousLanguages
          : nextLanguages;
      });

      return true;
    };

    const hasLanguagesImmediately = syncAvailableLanguages();

    if (!hasLanguagesImmediately) {
      pollIntervalId = window.setInterval(() => {
        if (syncAvailableLanguages() && pollIntervalId) {
          window.clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
      }, 300);
    }

    if (typeof MutationObserver !== "undefined" && document.body) {
      observer = new MutationObserver(() => {
        syncAvailableLanguages();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (pollIntervalId) {
        window.clearInterval(pollIntervalId);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (
      !hasLoadedTranslatorLanguages ||
      availableLanguages.some((language) => language.value === currentLang)
    ) {
      return;
    }

    const fallbackLanguage =
      availableLanguages.find(
        (language) => language.value === DEFAULT_GOOGLE_LANGUAGE
      )?.value || availableLanguages[0]?.value || DEFAULT_GOOGLE_LANGUAGE;

    setCurrentLang(fallbackLanguage);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        GOOGLE_TRANSLATE_STORAGE_KEY,
        fallbackLanguage
      );
      syncGoogleTranslateCookie(fallbackLanguage);
      window.dispatchEvent(
        new CustomEvent(GOOGLE_TRANSLATE_EVENT, {
          detail: { language: fallbackLanguage },
        })
      );
    }

    triggerGoogleTranslate(fallbackLanguage);
  }, [availableLanguages, currentLang, hasLoadedTranslatorLanguages]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      return undefined;
    }

    const focusTimeoutId = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimeoutId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const currentLanguageOption = useMemo(() => {
    const matchedLanguage = availableLanguages.find(
      (language) => language.value === currentLang
    );

    if (matchedLanguage) {
      return matchedLanguage;
    }

    return (
      GOOGLE_LANGUAGE_OPTIONS.find((language) => language.value === currentLang) ||
      buildLanguageOption(currentLang, currentLang)
    );
  }, [availableLanguages, currentLang]);

  const filteredLanguages = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return availableLanguages;
    }

    return availableLanguages.filter((language) => {
      const label = language.label.toLowerCase();
      const value = language.value.toLowerCase();

      return (
        label.includes(normalizedSearchTerm) ||
        value.includes(normalizedSearchTerm)
      );
    });
  }, [availableLanguages, searchTerm]);

  const applyLanguage = (nextLanguage) => {
    const normalizedLanguage = normalizeLanguageCode(nextLanguage);

    setCurrentLang(normalizedLanguage);
    setIsOpen(false);
    setSearchTerm("");

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        GOOGLE_TRANSLATE_STORAGE_KEY,
        normalizedLanguage
      );
      syncGoogleTranslateCookie(normalizedLanguage);
      window.dispatchEvent(
        new CustomEvent(GOOGLE_TRANSLATE_EVENT, {
          detail: { language: normalizedLanguage },
        })
      );
    }

    triggerGoogleTranslate(normalizedLanguage);
  };

  return (
    <div
      ref={wrapperRef}
      className={[
        "language-selector",
        "notranslate",
        isOpen ? "is-open" : "",
        disabled ? "is-disabled" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      translate="no"
    >
      <button
        type="button"
        className="language-selector__trigger"
        onClick={() => !disabled && setIsOpen((previousState) => !previousState)}
        aria-label="Translate page"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        translate="no"
      >
        <span className="language-selector__value">
          <span className="language-selector__flag" aria-hidden="true">
            <img
              src={currentLanguageOption.flag}
              alt=""
              className="language-selector__flag-image"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = flagLanguage;
              }}
            />
          </span>
          <span className="language-selector__label">
            {currentLanguageOption.label}
          </span>
        </span>
        <span className="language-selector__icon" aria-hidden="true">
          <IoIosArrowDown />
        </span>
      </button>

      {isOpen && (
        <div className="language-selector__menu">
          <div className="language-selector__search" translate="no">
            <span className="language-selector__search-icon" aria-hidden="true">
              <IoIosSearch />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className="language-selector__search-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search languages"
              aria-label="Search languages"
              translate="no"
            />
          </div>

          <div className="language-selector__options" role="listbox">
            {filteredLanguages.length > 0 ? (
              filteredLanguages.map((language) => {
                const isSelected = language.value === currentLang;

                return (
                  <button
                    key={language.value}
                    type="button"
                    className={`language-selector__option ${
                      isSelected ? "is-selected" : ""
                    }`}
                    onClick={() => applyLanguage(language.value)}
                    role="option"
                    aria-selected={isSelected}
                    translate="no"
                  >
                    <span className="language-selector__flag" aria-hidden="true">
                      <img
                        src={language.flag}
                        alt=""
                        className="language-selector__flag-image"
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = flagLanguage;
                        }}
                      />
                    </span>
                    <span className="language-selector__option-label">
                      {language.label}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="language-selector__empty" translate="no">
                No matching languages
              </div>
            )}
          </div>
        </div>
      )}

      {renderWidget ? (
        <div
          id={GOOGLE_TRANSLATE_ELEMENT_ID}
          className="google-translate-widget"
          style={{ display: "none" }}
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
};

export default GoogleTranslateSelect;
